import { useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { PatientMonitoringType, PatientStatus } from "../../../api";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { Select } from "../../../components/ui-kit/fields/select";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  monitoringTypes,
  patientStatuses,
} from "../../../constants/enum-to-text";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PhysiciansModal } from "../common/physicians-modal";
import { DurationEditor } from "./duration-editor";
import { parseDuration, serializeDuration } from "./duration-utils";
import { consentTypeValues } from "./monitoring-settings";

export const MonitoringSettingsForm = observer(function MonitoringSettingsForm({
  fields,
  admin,
  mode,
}: {
  fields: MonitoringSettingsFields;
  admin: AdminConsole;
  mode: "create" | "edit";
}) {
  const { t } = useLingui();
  return (
    <FormTable dataAdd>
      {mode === "create" && (
        <>
          <FormRow
            label={t`Monitoring type`}
            data={
              <Select
                attr="monitoring_type"
                model={fields}
                values={monitoringTypes}
                required
              />
            }
            mandatory
          />

          <FormRow
            label={t`Country`}
            data={
              <CountrySelect
                attr="country"
                model={fields}
                onChange={() => {
                  fields.region = null;
                }}
              />
            }
          />

          <FormRow
            label={t`Region`}
            data={
              <RegionSelect
                attr="region"
                model={fields}
                country={fields.country ?? undefined}
              />
            }
          />
        </>
      )}

      <FormRow
        label={t`New patient status`}
        data={
          <Select
            attr="new_patient_status"
            model={fields}
            values={patientStatuses}
          />
        }
      />

      <FormRow
        label={t`Consent type`}
        data={
          <Select
            attr="consent_type"
            model={fields}
            values={consentTypeValues}
          />
        }
      />

      <FormRow
        label={t`Auto deactivate after`}
        data={
          <DurationEditor
            value={parseDuration(fields.auto_deactivate_after)}
            onChange={(d) => {
              fields.auto_deactivate_after = d ? serializeDuration(d) : null;
            }}
          />
        }
      />

      <FormRow
        label={t`Auto schedule`}
        data={<Checkbox attr="auto_schedule" model={fields} />}
      />

      <FormRow
        label={t`Requires measurement config`}
        data={<Checkbox attr="requires_measurement_config" model={fields} />}
      />

      <FormRow
        label={t`Auto alerts`}
        data={<Checkbox attr="auto_alerts" model={fields} />}
      />

      <FormRow
        label={t`Is monitored`}
        data={<Checkbox attr="is_monitored" model={fields} />}
      />

      <FormRow
        label={t`Any new data activate`}
        data={<Checkbox attr="any_new_data_activate" model={fields} />}
      />

      <FormRow
        label={t`PPG/ECG new data activate`}
        data={<Checkbox attr="ppg_ecg_new_data_activate" model={fields} />}
      />

      <FormRow
        label={t`Status change email`}
        data={<Checkbox attr="status_change_email" model={fields} />}
      />

      {mode === "create" && (
        <>
          <FormRow
            label={t`Clinic`}
            data={
              <ChooseField
                count={fields["clinic_uuid"] ? 1 : 0}
                modal={({ onClose }) => {
                  const clinics = admin.createClinics();
                  return (
                    <ClinicsModal
                      title={t`Choose clinic`}
                      preselected={
                        fields["clinic_uuid"]
                          ? [fields["clinic_uuid"] as any]
                          : []
                      }
                      mode="single"
                      model={clinics}
                      onClose={onClose}
                      onConfirm={({ selected }) => {
                        fields["clinic_uuid"] = selected[0] ?? null;
                      }}
                    />
                  );
                }}
              />
            }
          />

          <FormRow
            label={t`Physician`}
            data={
              <ChooseField
                count={fields["physician_uuid"] ? 1 : 0}
                modal={({ onClose }) => {
                  const physicians = admin.createPhysicians();
                  return (
                    <PhysiciansModal
                      title={t`Choose physician`}
                      preselected={
                        fields["physician_uuid"]
                          ? [fields["physician_uuid"] as any]
                          : []
                      }
                      mode="single"
                      model={physicians}
                      onClose={onClose}
                      onConfirm={({ selected }) => {
                        fields["physician_uuid"] = selected[0] ?? null;
                      }}
                    />
                  );
                }}
              />
            }
          />
        </>
      )}
    </FormTable>
  );
});

export interface MonitoringSettingsFields {
  monitoring_type?: PatientMonitoringType | null;
  country?: string | null;
  region?: number | null;
  new_patient_status?: PatientStatus | null;
  auto_schedule?: boolean;
  requires_measurement_config?: boolean;
  auto_alerts?: boolean;
  consent_type?: number | null;
  is_monitored?: boolean;
  any_new_data_activate?: boolean;
  ppg_ecg_new_data_activate?: boolean;
  auto_deactivate_after?: string | null;
  status_change_email?: boolean;
  clinic_uuid?: string | null;
  physician_uuid?: string | null;
}
